/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, input, output, inject } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { PopoverModule } from 'primeng/popover';
import { AvatarModule } from 'primeng/avatar';
import { AppErrors } from '@fitness-force/models';
import type { ClientSubscriptionFreezeResponse, ClientSubscriptionFreezeTimeLineResponse, PaymentTimeline, UpdateClientSubscriptionFreezeRequest } from '@fitness-force/models';
import { ClientSubscriptionFreezeStatus, PlanCategory } from '@fitness-force/enum';
import { FreezeService, TimeconversionService,ToastMessageService } from '@fitness-force/api';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { ErrorsComponent, ErrorMsgComponent } from '@fitness-force/errors';

import { BehaviorSubject, Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { FreezeReasonService } from '../../services/freeze-reason.service';
import type { FreezeCycleAdjustmentResponse, CalculateFreezeAdjustmentRequest } from '../../models/freeze-reason.model';
import { RecurringTemplateAdjustmentType } from '../../enums/freeze.enum';

@Component({
  selector: 'freeze-edit',
  templateUrl: './freeze-edit.component.html',
  styleUrls: ['./freeze-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    DatePickerModule,
    SelectModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    AvatarModule,
    ErrorMsgComponent,
    ErrorsComponent,
    TranslateModule,
    AsyncPipe
]
})
export class FreezeEditComponent implements OnInit, OnDestroy {
  private freezeService = inject(FreezeService);
  private freezeReasonService = inject(FreezeReasonService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private toastMessageService = inject(ToastMessageService);
  private timeservice = inject(TimeconversionService);
  private readonly destroy$ = new Subject<void>();

  readonly visible = input(false);
  readonly freeze$ = input<BehaviorSubject<ClientSubscriptionFreezeResponse>>(undefined, { alias: "freeze" });
  readonly hidePopup = output<boolean>({ alias: 'hidePopup' });

  fg: UntypedFormGroup;
  freezeTimeline$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  clientId$: BehaviorSubject<number> = new BehaviorSubject(0);
  tenantId$: BehaviorSubject<number> = new BehaviorSubject(0);
  isUpdated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  freezeCycleAdjustment$ = new BehaviorSubject<FreezeCycleAdjustmentResponse | null>(null);
  freezeCycleAdjustmentLoading$ = new BehaviorSubject<boolean>(false);

  //#region error popup
  error = new BehaviorSubject(null as AppErrors | null);
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;
  btnLoading = new BehaviorSubject(false);

  HideErrorPopup(event: any) {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
    this.error.next(null);
  }

  ngOnInit(): void {
    this.componentInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  componentInit() {
    this.store.select(TENANTS_INFO).subscribe((tenant) => {
      this.tenantId$.next(tenant.selected_tenant.id);
    });

    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    const freeze$ = this.freeze$();
    if (!freeze$) {
      return;
    }
    if (this.IsRecurringSubscription) {
      this.fg = this.fb.group({
        selectedfreezeattempt: new UntypedFormControl('', [Validators.required]),
      });
    }
    else {
      this.fg = this.fb.group({
        startdate: new UntypedFormControl( this.timeservice.ConvertUTCToLocalDateTime(freeze$.value?.start_date || ''), [Validators.required]),
        enddate: new UntypedFormControl(this.timeservice.ConvertUTCToLocalDateTime(freeze$.value?.end_date || ''), [Validators.required]),
      });

      if(freeze$.value.status == ClientSubscriptionFreezeStatus.FreezeInprogress || freeze$.value.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress])
      {
        this.StartDateControl?.clearValidators();
      }
    }

    this.fg.addControl("comment", new UntypedFormControl(freeze$.value?.comment || '', [Validators.required]));

    this.clientId$.next(freeze$.value?.client_id || 0);

    this.freezeService.GetFreezeTimeline(this.clientId$.value, freeze$.value?.client_subscription.id || 0).subscribe({
      next: (freezeTimeline: PaymentTimeline) => {
          let timeline: any[] = [];
          freezeTimeline.invoice_occurance?.forEach((obj) => {
            let newObj: any = obj
            newObj.strDate = this.toDateHtml(this.timeservice.ConvertUTCToLocalDateTime(obj.occurance_start_date));
            newObj.strDateFormat = this.toDate(obj.occurance_start_date);
            const freeze$ = this.freeze$();
            if (freeze$ && this.toDate(obj.occurance_start_date) == this.toDate(freeze$.value?.start_date || '') && this.toDate(obj.occurance_end_date) == this.toDate(freeze$.value?.end_date || '')) {
              newObj.is_frozen = false;
              this.SelectedFreezeAttempt?.setValue(newObj);
            }
            timeline.push(newObj);
          });
          this.freezeTimeline$.next(timeline);
      },
      error: () => {
        this.freezeTimeline$.next(null);
      }
    });

    this.setupFormWatchers();

    setTimeout(() => {
      this.calculateFreezeCycleAdjustment();
    }, 500);
  }

  private setupFormWatchers(): void {
    if (this.IsRecurringSubscription) {
      // Recurring: re-calculate when a billing cycle is selected
      this.SelectedFreezeAttempt?.valueChanges
        .pipe(
          distinctUntilChanged(
            (prev, curr) =>
              prev?.occurance_start_date === curr?.occurance_start_date &&
              prev?.occurance_end_date === curr?.occurance_end_date
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.calculateFreezeCycleAdjustment();
        });
    } else {
      // Non-recurring: re-calculate when start or end date changes
      this.StartDateControl?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.EndDateControl?.value) {
            this.calculateFreezeCycleAdjustment();
          }
        });

      this.EndDateControl?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.StartDateControl?.value) {
            this.calculateFreezeCycleAdjustment();
          }
        });
    }
  }

  calculateFreezeCycleAdjustment(): void {
    const freeze$ = this.freeze$();
    if (!freeze$?.value) return;

    const freeze = freeze$.value;
    const tenantId = freeze.tenant_id;
    const clientId = freeze.client_id;
    const subscriptionId = freeze.client_subscription.id;

    if (!tenantId || !clientId || !subscriptionId) return;

    const startDate = this.IsRecurringSubscription
      ? this.SelectedFreezeAttempt?.value?.occurance_start_date || freeze.start_date
      : this.StartDateControl?.value || freeze.start_date;

    const endDate = this.IsRecurringSubscription
      ? this.SelectedFreezeAttempt?.value?.occurance_end_date || freeze.end_date
      : this.EndDateControl?.value || freeze.end_date;

    if (!startDate || !endDate) return;

    const payload: CalculateFreezeAdjustmentRequest = {
      requested_start_date: this.timeservice.ConvertLocalDateTimeToUTC(startDate),
      requested_end_date: this.timeservice.ConvertLocalDateTimeToUTC(endDate),
      adjustment_type: RecurringTemplateAdjustmentType.FreezeReschedule,
      freeze_id: freeze.freeze_id,
    };

    this.freezeCycleAdjustmentLoading$.next(true);

    this.freezeReasonService
      .calculateFreezeAdjustment(tenantId, clientId, subscriptionId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FreezeCycleAdjustmentResponse) => {
          this.freezeCycleAdjustment$.next(response);
          this.freezeCycleAdjustmentLoading$.next(false);
        },
        error: () => {
          this.freezeCycleAdjustment$.next(null);
          this.freezeCycleAdjustmentLoading$.next(false);
        },
      });
  }


  toDateHtml(d: Date | string): string {
    const dateStr = new Date(d).toDateString();
    const dateStrArr = dateStr.split(' ');
    return `<span>${this.ordinalNumber(dateStrArr[2] as unknown as number)}</span>
    <span> of </span>
    <span>${dateStrArr[1]}</span>
    <span>${dateStrArr[3]}</span>`;
  }

  toDate(d: Date | string): string {
    const dateStr = new Date(d).toDateString();
    const dateStrArr = dateStr.split(' ');
    const s = ['th', 'st', 'nd', 'rd'];
    const v: number = parseInt(dateStrArr[2]) % 100;
    return `${dateStrArr[2].toString() + (s[(v - 20) % 10] || s[v] || s[0]).toString()} of ${dateStrArr[1]} ${dateStrArr[3]}`;
  }

  ordinalNumber(n: number | null | undefined): string {
    if (n) {
      const s = ['th', 'st', 'nd', 'rd'];
      const v: number = n % 100;
      return (
        n.toString() +
        '<sup>' +
        (s[(v - 20) % 10] || s[v] || s[0]).toString() +
        '</sup> '
      );
    } else return '';
  }

  HidePopup() {
    this.hidePopup.emit(this.isUpdated$.value);
    this.hidePopup.emit(this.isUpdated$.value);
  }


  EditFreeze(): void {
    this.btnLoading.next(true);
    this.fg.markAllAsTouched();
    if (this.fg.valid) {
      const freeze$ = this.freeze$();
      if (!freeze$) {
        this.btnLoading.next(false);
        return;
      }
      let param = {} as UpdateClientSubscriptionFreezeRequest;
      if (this.IsRecurringSubscription) {
        const startDate = new Date(this.SelectedFreezeAttempt?.value.occurance_start_date);
        const endDate = new Date(this.SelectedFreezeAttempt?.value.occurance_end_date);
        startDate.setHours(0, 0, 0);
        endDate.setHours(23, 59, 59);
        param.start_date = this.timeservice.ConvertLocalDateTimeToUTC(this.SelectedFreezeAttempt?.value.occurance_start_date);
        param.end_date = this.timeservice.ConvertLocalDateTimeToUTC(this.SelectedFreezeAttempt?.value.occurance_end_date);
      }
      else {
        const startDate = new Date(this.StartDateControl?.value);
        const endDate = new Date(this.EndDateControl?.value);
        startDate.setHours(0, 0, 0);
        endDate.setHours(23, 59, 59);
        param.start_date = this.timeservice.ConvertLocalDateTimeToUTC(startDate);
        param.end_date = this.timeservice.ConvertLocalDateTimeToUTC(endDate);
      }
      param.comment = this.CommentControl?.value;
      (param as any).calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle =
        this.freezeCycleAdjustment$.value ?? undefined;
     
      this.freezeService.PutUpdateFreeze(
        freeze$.value?.tenant_id,
        freeze$.value?.client_id,
        freeze$.value?.client_subscription.id,
        freeze$.value?.freeze_id,
        param
      ).subscribe({
        next: (res) => {
          if (res) {
            this.isUpdated$.next(true);
            this.translate.get('FREEZE.FREEZE_EDIT.REQUEST_SUCCESS').subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            this.btnLoading.next(false);
            this.HidePopup();
            this.btnLoading.next(false);
          }
        },
        error: (err) => {
          this.error.next(err);
          this.showErrorPopup.next(true);
          this.btnLoading.next(false);
        }
      });
    }
    else{
      this.btnLoading.next(false);
    }
  }


  get StartDateControl(): AbstractControl | null {
    return this.fg.controls['startdate'] as AbstractControl;
  }

  get EndDateControl(): AbstractControl | null {
    return this.fg.controls['enddate'] as AbstractControl;
  }

  get SelectedFreezeAttempt(): AbstractControl | null {
    return this.fg.controls['selectedfreezeattempt'] as AbstractControl;
  }

  get CommentControl(): AbstractControl | null {
    return this.fg.controls['comment'] as AbstractControl;
  }

  get IsRecurringSubscription(): boolean {
    const freeze$ = this.freeze$();
    if (!freeze$) {
      return false;
    }
    const category: PlanCategory | null = freeze$.value?.client_subscription.category || null;

    if (category)
      return (
        category == PlanCategory.Contract || category.toString() == PlanCategory[PlanCategory.Contract]
        || category == PlanCategory.PayAsYouGo || category.toString() == PlanCategory[PlanCategory.PayAsYouGo]
      );
    else
      return false;
  }

  get isInProgresFreeze()
  {
    const freeze$ = this.freeze$();
    if (!freeze$) {
      return false;
    }
    return freeze$.value.status == ClientSubscriptionFreezeStatus.FreezeInprogress || freeze$.value.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress];
  }
}